import { useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useMetaPixel } from '../contexts/MetaPixelContext';

// LocalStorage keys (must match AnalyticsContext.js)
const ANONYMOUS_ID_KEY = 'op_anonymous_id';
const USER_ID_KEY = 'op_user_id';
const ALIASED_KEY = 'op_user_aliased';

/**
 * AnalyticsIdentify Component
 * 
 * Automatically identifies users in OpenPanel and Meta Pixel when:
 * - User logs in
 * - Session is restored from localStorage on page load
 * - User state changes for any reason
 * 
 * CRITICAL: This component calls alias() BEFORE identify() to ensure
 * anonymous activity from the current session is merged with the 
 * authenticated user profile. This fixes:
 * - Cross-session identification (returning users)
 * - Cross-device identification (same user, different device)
 * - Different login methods (WhatsApp signup, email login)
 */
const AnalyticsIdentify = () => {
    const { user } = useContext(AuthContext);
    const { identify, alias, reset, isInitialized } = useAnalytics();
    const { setAdvancedMatchingData, isEnabled: pixelEnabled } = useMetaPixel();

    useEffect(() => {
        // Wait for analytics to be ready
        if (!isInitialized) return;

        if (user?.id) {
            const userId = user.id.toString();
            const anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
            const wasAliased = localStorage.getItem(ALIASED_KEY) === userId;

            // CRITICAL: Alias anonymous → authenticated BEFORE identify
            // Only alias once per user to prevent duplicate alias calls
            if (anonId && anonId !== userId && !wasAliased) {
                console.log('[Analytics] 🔗 Aliasing anonymous session → user:', anonId, '→', userId);
                alias(anonId, userId);
            }

            // User is authenticated - identify them in OpenPanel
            console.log('[Analytics] 👤 Identifying user on session restore:', userId);
            identify(userId, {
                email: user.email,
                firstName: user.first_name,
                lastName: user.last_name,
                phone: user.phone || user.whatsapp,
                whatsapp_verified: user.whatsapp_verified,
            });

            // Store user ID for future sessions
            localStorage.setItem(USER_ID_KEY, userId);

            // Also set Advanced Matching data for Meta Pixel
            if (pixelEnabled) {
                setAdvancedMatchingData({
                    email: user.email,
                    phone: user.phone || user.whatsapp,
                    firstName: user.first_name,
                    lastName: user.last_name,
                    externalId: userId,
                });
            }
        } else {
            // User is not authenticated - anonymous session
            console.log('[Analytics] 👤 No user - anonymous session');
        }
    }, [user, isInitialized, identify, alias, reset, pixelEnabled, setAdvancedMatchingData]);

    // This component doesn't render anything
    return null;
};

export default AnalyticsIdentify;

